import { Box, Container, Stack, Typography } from '@mui/material';
import { memo } from 'react';
import CircleIcon from '@mui/icons-material/Circle';
import useInspection from 'hooks/useInspection';
import PositiveButton from 'components/cmn/PositiveButton';

function ClassificationGuide1(): JSX.Element {
  const { classification, changeIsClassificationGuideStart1 } = useInspection();
  const handleNext = () => {
    changeIsClassificationGuideStart1(true);
  };
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        py: 4,
      }}
    >
      <Box
        sx={{
          p: 4,
          width: '100%',
          maxWidth: { xs: '100%', md: '1536px' },
          height: { xs: 'auto', md: '864px' },
        }}
      >
        <Container maxWidth="xl" sx={{ height: '100%' }}>
          <Stack
            direction="column"
            justifyContent="center"
            alignContent="center"
            alignItems="center"
            sx={{ height: '100%' }}
          >
            <Box
              sx={{
                mb: 3,
                fontSize: { xs: '19px', sm: '48px', md: '52px' },
                fontWeight: 900,
                textAlign: 'center',
              }}
            >
              {classification}단계 분류 안내문 (전반적 의견 분류)
            </Box>

            <Container maxWidth="lg" sx={{ p: 0, height: 'calc(100% - 84px)' }}>
              <Typography
                sx={{
                  fontSize: { xs: '16px', sm: '18px', md: '20px' },
                  fontWeight: 400,
                  display: 'block',
                  lineHeight: { xs: '28px', sm: '32px', md: '35px' },
                  wordBreak: 'keep-all',
                }}
                variant="p"
                gutterBottom
              >
                1단계를 시작합니다.
                <br /> 이 단계에서는 제공된 문항에 대해 귀하의 의견을 4단계로 분류하는 과정입니다. 각 문항을 읽고 귀하의
                의견 수준을 명확하게 표현하여, 해당 항목에 대한 동의 정도를 평가하는 것입니다.
                <Box sx={{ height: 20 }} />
                <Typography
                  sx={{
                    fontSize: { xs: '18px', sm: '19px', md: '20px' },
                    fontWeight: 700,
                    lineHeight: { xs: '28px', sm: '32px', md: '35px' },
                  }}
                >
                  응답 방식 안내:
                </Typography>
                <Box sx={{ height: 10 }} />각 문항에 대해 다음의 4가지 선택지 중 하나를 선택해 주세요:
                <Box>
                  <Typography
                    sx={{
                      fontSize: { xs: '16px', sm: '18px', md: '20px' },
                      fontWeight: 400,
                      lineHeight: { xs: '28px', sm: '32px', md: '35px' },
                      '& .option-text': {
                        fontSize: { xs: '16px', sm: '18px', md: '20px' },
                      },
                    }}
                  >
                    <CircleIcon sx={{ fontSize: '6px', mx: '8px' }} />
                    <span className="option-text" style={{ color: '#007BFF', fontWeight: 700 }}>
                      전적으로 동의함&nbsp;:&nbsp;
                    </span>
                    <span style={{ wordBreak: 'keep-all' }}>
                      해당 문장에 대해 강한 확신을 가지며, 완전히 동의할 경우 선택합니다.
                    </span>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: '16px', sm: '18px', md: '20px' },
                      fontWeight: 400,
                      lineHeight: { xs: '28px', sm: '32px', md: '35px' },
                      '& .option-text': {
                        fontSize: { xs: '16px', sm: '18px', md: '20px' },
                      },
                    }}
                  >
                    <CircleIcon sx={{ fontSize: '6px', mx: '8px' }} />
                    <span className="option-text" style={{ color: '#008000', fontWeight: 700 }}>
                      동의함&nbsp;:&nbsp;
                    </span>
                    <span style={{ wordBreak: 'keep-all' }}>
                      대체로 동의하지만, 일부 고려할 요소나 의견의 차이가 있을 경우 선택합니다.
                    </span>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: '16px', sm: '18px', md: '20px' },
                      fontWeight: 400,
                      lineHeight: { xs: '28px', sm: '32px', md: '35px' },
                      '& .option-text': {
                        fontSize: { xs: '16px', sm: '18px', md: '20px' },
                      },
                    }}
                  >
                    <CircleIcon sx={{ fontSize: '6px', mx: '8px' }} />
                    <span className="option-text" style={{ color: '#FFA500', fontWeight: 700 }}>
                      동의하지 않음&nbsp;:&nbsp;
                    </span>
                    <span style={{ wordBreak: 'keep-all' }}>
                      대체로 해당 진술에 대해 부정적인 입장을 가지지만, 일부는 동의할 수 있다고 판단될 경우 선택합니다.
                    </span>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: '16px', sm: '18px', md: '20px' },
                      fontWeight: 400,
                      lineHeight: { xs: '28px', sm: '32px', md: '35px' },
                      '& .option-text': {
                        fontSize: { xs: '16px', sm: '18px', md: '20px' },
                      },
                    }}
                  >
                    <CircleIcon sx={{ fontSize: '6px', mx: '8px' }} />
                    <span className="option-text" style={{ color: '#FF0000', fontWeight: 700 }}>
                      전혀 동의하지 않음&nbsp;:&nbsp;
                    </span>
                    <span style={{ wordBreak: 'keep-all' }}>해당 문장에 대해 전혀 동의할 수 없을 경우 선택합니다.</span>
                  </Typography>
                </Box>
                <Box sx={{ height: 20 }} />
                <Box>
                  <Typography
                    sx={{
                      fontSize: { xs: '18px', sm: '19px', md: '20px' },
                      fontWeight: 700,
                      lineHeight: { xs: '28px', sm: '32px', md: '35px' },
                    }}
                  >
                    유의사항:
                  </Typography>
                  <Box sx={{ height: 10 }} />
                  <Typography
                    sx={{
                      fontSize: { xs: '16px', sm: '18px', md: '20px' },
                      fontWeight: 400,
                      lineHeight: { xs: '28px', sm: '32px', md: '35px' },
                      wordBreak: 'keep-all',
                    }}
                  >
                    <CircleIcon sx={{ fontSize: '6px', mx: '8px' }} />
                    <span style={{ fontWeight: 700 }}>문항의 의미를 다각도로 분석&nbsp;:&nbsp;</span>
                    <span style={{ wordBreak: 'keep-all' }}>
                      문장을 단순히 읽고 판단하는 것이 아니라, 자신의 경험과 비교하며 문장에서 의미하는 요소들을 신중히
                      검토해 주세요. 예를 들어,&nbsp;
                    </span>
                    <span style={{ color: '#FF0000', wordBreak: 'keep-all' }}>
                      문장의 일부 표현이 자신의 경험과 완전히 일치하는지, 아니면 특정 상황에서만 해당되는지를 따져보아야
                      합니다.
                    </span>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: '16px', sm: '18px', md: '20px' },
                      fontWeight: 400,
                      lineHeight: { xs: '28px', sm: '32px', md: '35px' },
                    }}
                  >
                    <CircleIcon sx={{ fontSize: '6px', mx: '8px' }} />
                    <span style={{ fontWeight: 700 }}>문항 간 차이를 세밀하게 구분&nbsp;:&nbsp;</span>
                    <span style={{ wordBreak: 'keep-all' }}>
                      유사한 문항이 있을 수 있지만, 세부적인 차이를 파악하고 본인의 의견을 가장 적합한 의견을 선택해
                      주세요.&nbsp;
                    </span>
                    <span style={{ color: '#FF0000', wordBreak: 'keep-all' }}>
                      문장의 일부 표현이 자신의 경험과 완전히 일치하는지, 아니면 특정 상황에서만 해당되는지를 따져보아야
                      합니다.
                    </span>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: '16px', sm: '18px', md: '20px' },
                      fontWeight: 400,
                      lineHeight: { xs: '28px', sm: '32px', md: '35px' },
                    }}
                  >
                    <CircleIcon sx={{ fontSize: '6px', mx: '8px' }} />
                    <span style={{ fontWeight: 700, wordBreak: 'keep-all' }}>충분한 시간 할애&nbsp;:&nbsp;</span>
                    <span style={{ wordBreak: 'keep-all' }}>
                      빠른 응답보다 각 문항에 대한 충분한 숙고를 권장합니다.
                    </span>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: '16px', sm: '18px', md: '20px' },
                      fontWeight: 400,
                      lineHeight: { xs: '28px', sm: '32px', md: '35px' },
                    }}
                  >
                    <CircleIcon sx={{ fontSize: '6px', mx: '8px' }} />
                    <span style={{ fontWeight: 700 }}>개인의 경험과 실제 상황 반영&nbsp;:&nbsp;</span>
                    <span>객관적인 판단보다는,&nbsp;</span>
                    <span style={{ color: '#FF0000', wordBreak: 'keep-all' }}>
                      개인의 경험과 신념을 기반으로 문항을 평가&nbsp;
                    </span>
                    <span>해 주세요.</span>
                  </Typography>
                </Box>
                <Box sx={{ height: 10 }} />
              </Typography>
            </Container>
          </Stack>
        </Container>
      </Box>
      {/* 하단 버튼 영역 높이만큼 여백 추가 (버튼 높이 52px + 패딩 32px = 약 84px) */}
      <Box sx={{ height: { xs: 84, sm: 84 } }} />
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100%',
          bgcolor: 'background.paper',
          boxShadow: '0px -2px 10px rgba(0, 0, 0, 0.1)',
          py: 2,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <PositiveButton
          variant="contained"
          onClick={handleNext}
          sx={{ width: { xs: 130 }, height: 52, typography: 's14w500' }}
          title="시작"
        />
      </Box>
    </Box>
  );
}

export default memo(ClassificationGuide1);
